"use client";

import { useEffect, useState } from "react";
import { TwtBrand } from "~/components/TwtBrand";
import { getLeviathanCountdown, type LeviathanCountdown } from "~/components/home/leviathanCountdown";
import { LeviathanStageCountdown } from "~/components/home/LeviathanStageCountdown";

import styles from "./LeviathanOverlay.module.css";

type ArtworkProps = { src: string; srcSet?: string; sizes?: string };

// The Project Leviathan countdown that covers the home page during the release
// hold. The home page loads it only while the hold is on, so visitors after the
// release never download it.

// The on-screen width of a wall element that is `photoPx` photo px wide, for
// the browser's choice of image variant: on phones the photo covers a band
// 107.4vw tall (a photo px is 1.074vw / 2160), on desktop it covers the
// viewport (the larger of width / 3840 and height / 2160).
function wallSizesForPhotoPx(photoPx: number): string {
  const phone = `${(photoPx * 0.0497).toFixed(2)}vw`;
  return `(max-width: 900px) ${phone}, (max-aspect-ratio: 4/3) ${phone}, max(${(photoPx / 38.4).toFixed(2)}vw, ${(photoPx / 21.6).toFixed(2)}vh)`;
}

export default function LeviathanOverlay({
  initialRemaining,
  initialTargetMs,
  initialArtworkProps,
  memberAccessHeld,
}: {
  initialRemaining: LeviathanCountdown;
  initialTargetMs: number;
  initialArtworkProps?: ArtworkProps | null;
  memberAccessHeld: boolean;
}) {
  const [targetMs, setTargetMs] = useState(initialTargetMs);
  const [artworkProps, setArtworkProps] = useState(initialArtworkProps);
  const [remaining, setRemaining] = useState<LeviathanCountdown>(
    initialRemaining
  );

  useEffect(() => {
    const tick = () => setRemaining(getLeviathanCountdown(Date.now(), targetMs));
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [targetMs]);

  useEffect(() => {
    let stopped = false;
    let pending: AbortController | null = null;
    const refresh = async () => {
      if (stopped || pending || document.visibilityState === "hidden") return;
      const controller = new AbortController();
      pending = controller;
      const timeout = window.setTimeout(() => controller.abort(), 3_000);
      try {
        const response = await fetch("/api/leviathan-countdown", {
          signal: controller.signal,
          cache: "no-store",
        });
        if (!response.ok) return;
        const value: unknown = await response.json();
        const next = value && typeof value === "object" && "targetMs" in value
          ? value.targetMs : null;
        if (!stopped && typeof next === "number" &&
          Number.isSafeInteger(next) && next >= 0) {
          setTargetMs(next);
        }
        const artwork = value && typeof value === "object" && "artworkProps" in value
          ? value.artworkProps : null;
        if (!stopped && artwork && typeof artwork === "object" && "src" in artwork &&
          typeof artwork.src === "string") {
          setArtworkProps({
            src: artwork.src,
            srcSet: "srcSet" in artwork && typeof artwork.srcSet === "string" ? artwork.srcSet : undefined,
            sizes: "sizes" in artwork && typeof artwork.sizes === "string" ? artwork.sizes : undefined,
          });
        }
      } catch {
        // Keep the last displayed target and the hold if the read fails.
      } finally {
        window.clearTimeout(timeout);
        pending = null;
      }
    };
    void refresh();
    const interval = window.setInterval(() => void refresh(), 5_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      stopped = true;
      pending?.abort();
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  const artwork = artworkProps?.src.includes("%2Fpublic%2Fleviathan%2F") || artworkProps?.src.includes("/public/leviathan/") ? artworkProps : undefined;

  return (
    <div
      className={styles.overlay + " leviathan-overlay"}
      role="dialog"
      aria-modal="true"
      aria-labelledby="launch-title"
    >
      {/* The title, countdown and line are image layers on the LED wall in
          the photo; the wall's first slice is the page's accessible timer. */}
      <h1 id="launch-title" className="sr-only">Project Leviathan, Vol Bot 2.5</h1>
      <div className="overlay-shell">
        <header className="overlay-header">
          <TwtBrand
            href="/"
            size="md"
            invertedMark
            className="overlay-brand"
            textClassName="text-[21px] text-[#f4ebdc]"
            emphasisClassName="text-[#d97757]"
          />
          <a className="header-dashboard" href="/dashboard">
            <span>{memberAccessHeld ? "Returning Members" : "Dashboard"}</span>
            <span className="dashboard-chevron" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" focusable="false">
                <path d="m9 5 7 7-7 7" />
              </svg>
            </span>
          </a>
        </header>

        {/* The stage photo with the countdown drawn live onto its LED wall
            the one button. On desktop the photo fills the viewport and the
            button sits on the stage floor below the wall. On phones the photo
            is a band and the button follows it in the page flow, the way
            event pages stack a hero and its button on a phone. */}
        <div className="launch-stage">
          <div className="launch-artwork">
            <LeviathanStageCountdown
              src={artwork?.src}
              srcSet={artwork?.srcSet}
              sizes={artwork?.sizes}
              targetMs={targetMs}
              initialRemaining={initialRemaining}
              remaining={remaining}
              position={[0.56, 0]}
              liveText="all"
              sizesForPhotoPx={wallSizesForPhotoPx}
            />
          </div>
          <section className="launch-content">
            <a className="member-dashboard" href="/pricing" data-userflow="pricing-link">
              <span className="announcement-label">Limited availability</span>
              <span aria-hidden="true">→</span>
            </a>
          </section>
        </div>
      </div>

    </div>
  );
}
